import { Input, Output, EventEmitter, Component, OnInit, OnDestroy } from '@angular/core';
import { Location } from '@angular/common';
import { Subject, Observable, defer, Subscription } from 'rxjs';
import { AbstractDetailsComponent } from '../../shared/abstract-details.component';
import { ActivatedRoute } from '@angular/router';
import { NgForm, UntypedFormGroup, UntypedFormControl, Validators, ControlContainer } from '@angular/forms';
import { QuickeditService } from '../../../services/quickedit.service';
import { ClipboardService } from 'ngx-clipboard';
import { QueryParameter } from '../../../interfaces/queryString';
import { SapiQueryResponse, SapiEntity } from '../../../services/sapi.service';
import { debounceTime } from 'rxjs/operators';
import { Adunit } from './adunit-list.component';
import { Playout } from '../../../interfaces/playout';

export interface AdunitSapiEntity extends SapiEntity {
	positionType?: string;
	playout?: Playout;
}

@Component({
	selector: 'adunit-quickedit',
	templateUrl: './adunit-quickedit.component.html',
	styleUrls: ['./adunit-quickedit.component.scss'],
	viewProviders: [{
		provide: ControlContainer,
		useExisting: NgForm
	}]
})

export class AdunitQuickeditComponent extends AbstractDetailsComponent implements OnInit, OnDestroy {
	@Input() parentForm: NgForm;

	@Input() get row() {
		return this._row;
	}
	set row(value) {
		this._row = value;
	}

	@Input() isNew: boolean;
	@Input() unlink: boolean;
	@Input() noQuickeditService: boolean;

	@Input() index: number;

	@Output() saved: EventEmitter<any> = new EventEmitter<any>();
	@Output() removed: EventEmitter<any> = new EventEmitter<any>();
	@Output() unlinked: EventEmitter<any> = new EventEmitter<any>();
	@Output() canceled: EventEmitter<any> = new EventEmitter<any>();
	@Output() rowChange = new EventEmitter();

	_row: any;

	entity: AdunitSapiEntity;

	label = 'AD_UNIT';
	saveAction: Observable<any>;

	disabled: boolean;
	allowSkipping: boolean;

	date: Date;
	fields: string[];

	formGroup: UntypedFormGroup;
	formGroupName: string;

	subscriptions: Subscription;

	constructor(protected route: ActivatedRoute,
		protected quickeditService: QuickeditService,
		protected clipboardService: ClipboardService,
		protected location: Location) {
		super(route, location);

		this.entityConfiguration = {
			name: 'adunit',
			entityTranslation: this.translate.instant('AD_UNIT'),
			standAlone: true,
			parentPath: '/adunits/adunit',
			basePath: '/adunits/adunits/'
		};

		this.fields = ['status', 'title', 'code', 'playout-commercialBehaviour', 'playout-minClipDurationPreroll', 'playout-minClipDurationPostroll',
			'playout-skipOffset', 'playout-skipCounterText', 'playout-skipButtonText', 'playout-upcomingText', 'playout-upcomingVideo'];

		this.subscriptions = new Subscription();

		this.date = new Date();

		this.formGroupName = 'adunit-quickedit-' + this.date.getTime();

		this.formGroup = new UntypedFormGroup({});

		this.formGroup.addControl('title', new UntypedFormControl('', { validators: Validators.required }));

		this.fields.forEach((formFieldName: string) => {
			if (formFieldName !== 'title') {
				this.formGroup.addControl(formFieldName, new UntypedFormControl(''));
			}
		});
	}

	ngOnInit() {
		if (this.parentForm) {
			this.parentForm.form.addControl(this.formGroupName, this.formGroup);
		}

		if (this.row && this.row.data) {
			this.entity = this.row.data;
		}
		super.ngOnInit();

		if (this.noQuickeditService === undefined || !this.noQuickeditService) {
			this.saveAction = defer(() => this.save(true));
			this.quickeditService.addSaveAction(this.saveAction);
		}

		if (this.entity) {
			const adunit: Adunit = this.entity as Adunit;
			this.fields.forEach((fieldName: string) => {
				const formControl: UntypedFormControl = this.formGroup.get(fieldName) as UntypedFormControl;
				if (fieldName === 'code' && adunit.id) {
					formControl.disable();
				}
				if (fieldName.startsWith('playout-')) {
					if (!adunit.playout) {
						adunit.playout = {} as Playout;
					}
					const playoutFieldName = fieldName.replace('playout-', '');
					if (adunit.playout[playoutFieldName]) {
						formControl.setValue(adunit.playout[playoutFieldName]);
					}
				} else {
					if (this.entity[fieldName]) {
						formControl.setValue(this.entity[fieldName]);
					}
				}
			});
		}

		this.subscriptions.add(
			this.formGroup.get('title').valueChanges.subscribe((title: string) => {
				this.titleUpdated(title);
			})
		);

		this.subscriptions.add(
			this.formGroup.get('code').valueChanges.pipe(
				debounceTime(400)
			).subscribe((code: string) => {
				this.codeUpdated(code);
			})
		);

		this.subscriptions.add(
			this.formGroup.valueChanges.pipe(
				debounceTime(400)
			).subscribe((fields: any) => {
				const fieldsUpdate = {};
				const fieldsUpdatePlayout = {};
				Object.keys(fields).forEach((field: string) => {
					if (field.startsWith('playout-')) {
						const playoutField = field.replace('playout-', '');
						fieldsUpdatePlayout[playoutField] = fields[field];
					} else {
						fieldsUpdate[field] = fields[field];
					}
				});
				this._row.data = { ...this._row.data, ...fieldsUpdate };
				this._row.data.playout = { ...this._row.data.playout, ...fieldsUpdatePlayout };
				this.entity = this._row.data;
				this.rowChange.emit(this._row);
			})
		);
		this.updateSkipOffset(true);
	}

	ngOnDestroy() {
		if (this.parentForm) {
			this.parentForm.form.removeControl(this.formGroupName);
		}

		if (this.subscriptions) {
			this.subscriptions.unsubscribe();
		}
	}

	updateSkipOffset(fromEntity = false) {
		const entity = this.entity as Adunit;
		if (entity && entity.playout) {
			if (fromEntity) {
				this.allowSkipping = entity.playout.skipOffset >= 0;
			} else {
				entity.playout.skipOffset = this.allowSkipping ? 5 : -1;
				const formControl: UntypedFormControl = this.formGroup.get('playout-skipOffset') as UntypedFormControl;
				formControl.markAsTouched();
				formControl.markAsDirty();
			}
		}
	}

	saveQuickEdit() {
		this.save(true).subscribe(() => {
			this.removeSaveAction();
		});
	}

	save(force: boolean): Observable<any> {
		const saveSubject = super.save(force, null, false);

		saveSubject.subscribe((response) => {
			if (this.isNew) {
				this.row.id = this.entity.id;
				this.row.data = this.entity;
				this.saved.emit(true);
			}
			this.row.quickEditing = false;
			this.resetEntity();
		});
		return saveSubject;
	}

	removeSaveAction() {
		if (this.saveAction) {
			this.quickeditService.removeSaveAction(this.saveAction);
		}
	}

	delete(): Subject<void> {
		if (this.unlink) {
			this.removeSaveAction();
			this.unlinked.emit();
		} else {
			const deleteSubject = super.delete();

			deleteSubject.subscribe((response) => {
				this.removed.emit();
			});
			this.isNew = true;
			return deleteSubject;
		}
	}

	resetEntity() {
		this.removeSaveAction();
		this.cancel().subscribe((entity) => {
			this.row.data = entity;
			this.entity = entity;
			this.updateSkipOffset(true);
		});
	}

	onCancelClick() {
		if (this.isNew) {
			this.canceled.emit(true);
			this.removeSaveAction();
		} else {
			this.resetEntity();
			this.row.quickEditing = false;
		}
	}

	titleUpdated(title: string) {
		this.row.data.title = title;
		this.codeUpdated(title);
	}

	codeUpdated(code: string) {
		if (!this.row.id) {
			this.row.data.code = code ? code.replace(/-/g, '*').replace(/[^\w\-]{1,}/g, '_').toLowerCase() : '';

			const formControl: UntypedFormControl = this.formGroup.get('code') as UntypedFormControl;
			formControl.setValue(this.row.data.code, { emitEvent: false });

			const queryParams: QueryParameter[] = [{ name: 'q', values: ['code:' + this.row.data.code] }];
			this.sapiService.query('adunit', queryParams).subscribe((sapiQueryResponse: SapiQueryResponse) => {
				if (sapiQueryResponse && sapiQueryResponse.totalAmount > 0) {
					formControl.setErrors({ 'not-unique': true });
					formControl.markAsTouched();
					formControl.markAsDirty();
				}
			});
		}
	}
}
